import React, { useCallback, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import get from 'lodash.get'
import { useFormattingContext } from '../../../FormattingProvider/FormattingContext'
import ComponentLine from '../../../../charts/echarts/ComponentLine'
import { useChartContext } from '../../../../charts/echarts/ChartProvider'
import { usePipelineDataContextWithVariables } from '../../provider/DDPDataProvider'

const useFormatters = (valueFormat, categoryFormat) => {
  const { formatter } = useFormattingContext()

  const valueFormatter = useCallback(d => formatter(d, valueFormat), [formatter, valueFormat])
  const categoryFormatter = useCallback(d => formatter(d, categoryFormat), [formatter, categoryFormat])

  return {
    valueFormatter,
    categoryFormatter
  }
}

function PipelineComponentLine ({
  pipeline,
  variables,
  seriesAccessor,
  xAccessor,
  yAccessor,
  valueFormat,
  categoryFormat,
  ...seriesProps
}) {
  const { valueFormatter, categoryFormatter } = useFormatters(valueFormat, categoryFormat)
  const { setSeriesLoading } = useChartContext()
  const { data, isLoading } = usePipelineDataContextWithVariables(pipeline, variables)
  const chartData = useMemo(() => {
    const seriesData = get(data, seriesAccessor)
    const series = (seriesData || []).map(item => [get(item, xAccessor), get(item, yAccessor)])

    return {
      series
    }
  }, [data, seriesAccessor, xAccessor, yAccessor])

  useEffect(() => {
    setSeriesLoading(seriesProps.seriesId, isLoading)
  }, [seriesProps.seriesId, isLoading, setSeriesLoading])

  if (isLoading) return null
  return (
    <ComponentLine
      data={chartData?.series || []}
      valueFormat={valueFormatter}
      categoryFormat={categoryFormatter}
      {...seriesProps}
    />
  )
}

PipelineComponentLine.propTypes = {
  /** The data pipeline to execute */
  pipeline: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  variables: PropTypes.object,
  seriesAccessor: PropTypes.string,
  xAccessor: PropTypes.string,
  yAccessor: PropTypes.string,
  valueFormat: PropTypes.string,
  categoryFormat: PropTypes.string
}

PipelineComponentLine.defaultProps = {
  valueFormat: 'marketValue',
  categoryFormat: 'date',
  height: 300
}

export default PipelineComponentLine
